/**
 * URL utility functions for consistent URL handling across the application.
 *
 * These utilities match the backend normalization logic in url_utils.py
 * to ensure consistent URL handling between frontend and backend.
 */

/**
 * Normalize a URL by removing trailing slashes for consistency.
 *
 * This ensures URLs are compared consistently regardless of trailing slash.
 * For example, "https://example.com" and "https://example.com/" are treated
 * as the same URL.
 *
 * @param url - The URL to normalize
 * @returns Normalized URL without trailing slash
 *
 * @example
 * ```ts
 * normalizeUrl("https://example.com/") // "https://example.com"
 * normalizeUrl("https://example.com/page/") // "https://example.com/page"
 * normalizeUrl("https://example.com") // "https://example.com"
 * ```
 */
export function normalizeUrl(url: string): string {
  if (!url || typeof url !== 'string') {
    return url;
  }

  const trimmed = url.trim();

  // Remove trailing slashes for consistency
  // e.g., "https://d5c77c-57.myshopify.com/" becomes "https://d5c77c-57.myshopify.com"
  // e.g., "https://example.com/page/" becomes "https://example.com/page"
  return trimmed.replace(/\/+$/, '');
}

/**
 * Check if a URL is globally authorized for scraping in any campaign.
 *
 * This function checks all campaigns in the app configuration to see if the
 * given URL has flouri_scraping_enabled set to true anywhere. This enables
 * URL authorization to be shared across campaigns - if a URL is authorized
 * in one campaign, it should be considered authorized everywhere.
 *
 * @param url - The URL to check authorization for
 * @param appConfig - The app configuration object containing campaign_list
 * @returns true if the URL is authorized in any campaign, false otherwise
 *
 * @example
 * ```ts
 * const isAuthorized = isUrlGloballyAuthorized('https://example.com', appConfig);
 * ```
 */
export function isUrlGloballyAuthorized(
  url: string,
  appConfig: { configuration?: { campaign_list?: Array<{
    asset_group_list?: Array<{
      final_urls?: Array<{ url: string; flouri_scraping_enabled?: boolean } | string>;
    }>;
    ads_group_list?: Array<{
      ads?: Array<{ final_urls?: Array<{ url: string; flouri_scraping_enabled?: boolean } | string> }>;
      ads_list?: Array<{ final_urls?: Array<{ url: string; flouri_scraping_enabled?: boolean } | string> }>;
      keywords_list?: Array<{ final_urls?: Array<{ url: string; flouri_scraping_enabled?: boolean } | string> }>;
    }>;
  }> } } | null | undefined
): boolean {
  if (!appConfig?.configuration?.campaign_list || !url) {
    return false;
  }

  const normalizedUrl = normalizeUrl(url);

  for (const camp of appConfig.configuration.campaign_list) {
    // Check asset groups (Performance Max campaigns)
    for (const ag of camp.asset_group_list || []) {
      for (const urlObj of ag.final_urls || []) {
        if (typeof urlObj === 'object' && normalizeUrl(urlObj.url) === normalizedUrl && urlObj.flouri_scraping_enabled) {
          return true;
        }
      }
    }

    // Check ads and keywords (Search/Shopping/Display campaigns)
    for (const adGroup of camp.ads_group_list || []) {
      // Check ads
      for (const ad of adGroup.ads_list || adGroup.ads || []) {
        for (const urlObj of ad.final_urls || []) {
          if (typeof urlObj === 'object' && normalizeUrl(urlObj.url) === normalizedUrl && urlObj.flouri_scraping_enabled) {
            return true;
          }
        }
      }
      // Check keywords
      for (const keyword of adGroup.keywords_list || []) {
        for (const urlObj of keyword.final_urls || []) {
          if (typeof urlObj === 'object' && normalizeUrl(urlObj.url) === normalizedUrl && urlObj.flouri_scraping_enabled) {
            return true;
          }
        }
      }
    }
  }

  return false;
}

/**
 * Format GSC site URL for display.
 *
 * Handles both domain properties (sc-domain:example.com) and
 * URL prefix properties (https://example.com/).
 *
 * @param siteUrl - The GSC site URL to format
 * @returns Formatted display string
 */
export function formatSiteUrl(siteUrl: string): string {
  if (!siteUrl) return '';
  if (siteUrl.startsWith('sc-domain:')) {
    return siteUrl.substring(10);
  }
  try {
    const url = new URL(siteUrl);
    let host = url.hostname;
    if (host.startsWith('www.')) {
      host = host.substring(4);
    }
    const path = url.pathname;
    return path && path !== '/' ? host + path : host;
  } catch {
    return siteUrl;
  }
}
